import React, { useState, useEffect } from "react";
import { ArrowLeft, Loader2, User, Store, Clock, MapPin } from "lucide-react";
import getBankInfo from '../../../../services/service';
import { formatCurrency, formatDateTime } from '../../../../utils/format';

import {
  customerService,
  merchantService,
} from '../../../../services/apiService';

const CustomerDetail = ({ customerAccount, onBack }) => {
  const [customerInfo, setCustomerInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [merchantMap, setMerchantMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerAccount) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Gọi API tuần tự dùng service
        const detailRes = await customerService.getDetail(customerAccount.customerAccount, customerAccount.bankCode).catch(e => {
          console.warn("Lỗi getDetail:", e.message);
          return { data: {} };
        });
        const txRes = await customerService.getTransactions(customerAccount.customerAccount, { limit: 20 }).catch(e => {
          console.warn("Lỗi getTransactions:", e.message);
          return { data: [] };
        });

        const customerData = detailRes.data;
        setCustomerInfo(customerData);
        setTransactions(txRes.data || []);

        // 2. Xử lý logic Merchant
        const merchants = customerData.merchants || [];
        const terminalIDs = [...new Set(merchants.map((m) => m.merchant_id))];

        if (terminalIDs.length > 0) {
          // Gọi service lấy info merchant
          const merchantRes = await merchantService.getInfo(terminalIDs);
          const metadata = merchantRes.data?.metadata || []; // Axios trả về data.metadata

          const map = {};
          metadata.forEach((item) => {
            map[item.terminalID] = item;
          });
          setMerchantMap(map);
        }
      } catch (error) {
        console.error("Lỗi tải dữ liệu:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customerAccount]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-blue-600 font-medium">
          <Loader2 className="animate-spin" /> Đang tải hồ sơ...
        </div>
      </div>
    );
  }

  if (!customerInfo) return null;

  const bankInfo = getBankInfo(customerInfo.bank_code);
  const merchants = customerInfo.merchants || [];

  return (
    <div className="animate-fade-in-up space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            Hồ sơ khách hàng
          </h2>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>
              Tài khoản:{" "}
              <span className="font-mono font-bold text-blue-600">
                {customerInfo.customer_account}
              </span>
            </span>
            <span>•</span>
            <span>{bankInfo.name}</span>
          </div>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row gap-6 justify-between items-center">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg shadow-indigo-200 flex-shrink-0">
              {customerInfo.customerName ? (
                customerInfo.customerName.charAt(0)
              ) : (
                <User size={32} />
              )}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-900">
                {customerInfo.customerName || "Khách vãng lai"}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`px-2 py-0.5 text-xs font-bold rounded ${bankInfo.bg} ${bankInfo.color} border border-transparent`}
                >
                  {bankInfo.name}
                </span>
                <span className="text-sm text-slate-500 font-mono">
                  {customerInfo.customer_account}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-8 w-full md:w-auto border-t md:border-t-0 border-slate-100 pt-4 md:pt-0">
            <div className="text-right flex-1 md:flex-none">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                Tổng chi tiêu
              </p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(customerInfo.totalAmount)}
              </p>
            </div>
            <div className="h-12 w-px bg-slate-200 hidden md:block"></div>
            <div className="text-right flex-1 md:flex-none">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                Tổng số giao dịch
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {customerInfo.number_transaction?.toLocaleString("vi-VN")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* --- Merchant History --- */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
          <div className="p-5 border-b border-slate-200 bg-slate-50">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Store size={18} className="text-indigo-600" /> Lịch sử tiêu dùng
              theo Cửa hàng
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[500px]">
            {merchants.map((m, idx) => {
              const info = merchantMap[m.merchant_id] || {};
              // Logic hiển thị tên: Có tên thì hiện tên, không có thì hiện ID
              const hasName = info.workspaceName || info.userName;
              const displayName = hasName || m.merchant_id;
              const displayAddress = info.merchantAddress || info.userAddress;

              return (
                <div
                  key={idx}
                  className="p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 pr-4">
                      {/* Tên Cửa hàng (ID) */}
                      <div className="flex items-start gap-2">
                        <Store
                          size={14}
                          className="text-indigo-600 flex-shrink-0 mt-1"
                        />
                        <div>
                          <p
                            className="font-bold text-slate-800 text-sm line-clamp-2"
                            title={`${displayName} (${m.merchant_id})`}
                          >
                            {displayName}
                            {/* Nếu có tên thì mới hiển thị ID trong ngoặc, tránh bị lặp (MBBMS... (MBBMS...)) */}
                            {hasName && (
                              <span className="text-slate-500 font-normal font-mono ml-1">
                                ({m.merchant_id})
                              </span>
                            )}
                          </p>

                          {/* Địa chỉ */}
                          {displayAddress && (
                            <p className="text-xs text-slate-500 mt-1 flex items-start gap-1">
                              <MapPin
                                size={10}
                                className="mt-0.5 flex-shrink-0"
                              />
                              <span className="line-clamp-1">
                                {displayAddress}
                              </span>
                            </p>
                          )}

                          <p className="text-[10px] text-slate-400 mt-1 italic">
                            GD cuối: {formatDateTime(m.last_tx_time)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-slate-900 text-sm">
                        {formatCurrency(m.totalAmount)}
                      </p>
                      <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded inline-block mt-1">
                        {m.number_transaction} GD
                      </span>
                    </div>
                  </div>

                  {/* Thanh phần trăm */}
                  <div className="relative w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
                    <div
                      className="absolute top-0 left-0 h-full bg-indigo-500 rounded-full"
                      style={{
                        width: `${(m.totalAmount / customerInfo.totalAmount) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
            {merchants.length === 0 && (
              <div className="p-8 text-center text-slate-500 italic">
                Chưa có dữ liệu cửa hàng.
              </div>
            )}
          </div>
        </div>

        {/* --- Transaction History --- */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
          <div className="p-5 border-b border-slate-200 bg-slate-50">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Clock size={18} className="text-blue-600" /> Giao dịch gần nhất
              (Max 20)
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[500px]">
            <table className="w-full text-left text-sm">
              <thead className="bg-white text-slate-500 text-xs uppercase font-semibold sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3 bg-slate-50">Thời gian</th>
                  <th className="px-4 py-3 bg-slate-50">Merchant</th>
                  <th className="px-4 py-3 bg-slate-50 text-right">Số tiền</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.length > 0 ? (
                  transactions.map((txn, idx) => {
                    const info = merchantMap[txn.merchant_id] || {};
                    const hasName = info.workspaceName || info.userName;
                    const name = hasName || txn.merchant_id;

                    return (
                      <tr
                        key={idx}
                        className="hover:bg-blue-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                          {formatDateTime(txn.event_time)}
                        </td>

                        {/* CỘT MERCHANT: Tên (ID) */}
                        <td className="px-4 py-3">
                          <span
                            className="font-bold text-slate-800 text-xs line-clamp-1"
                            title={`${name} (${txn.merchant_id})`}
                          >
                            {name}
                            {hasName && (
                              <span className="text-slate-500 font-normal font-mono ml-1">
                                ({txn.merchant_id})
                              </span>
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-right font-medium text-slate-900 whitespace-nowrap">
                          {formatCurrency(txn.amount)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-4 py-8 text-center text-slate-500 italic"
                    >
                      Chưa có dữ liệu giao dịch.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
